import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { type CellContext, RowActionsWrapper } from "@msuite/ui/table";
import { Tooltip } from "@msuite/ui/tooltip";
import { getPayableAmounts, isIncomingInvoiceLocked } from "@msuite/utils/incoming-invoices";
import React from "react";
import { useCitadel } from "@/hooks/use-citadel";
import { EditCostAllocationModal } from "@/modals/incoming-invoices/edit-cost-allocation-modal";
import type { CostAllocationRow } from "./cost-allocation-row";

/**
 * A row's edit, which opens the invoice's whole allocation: the rows add up to the net amount
 * only together, so no single row is changed on its own. Hidden once the invoice is approved or paid and
 * for a caller without `update IncomingInvoice`, as the edit beside the search.
 */
export function CostAllocationRowActions({ cell }: CellContext<CostAllocationRow, "actions">) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { citadel } = useCitadel();
	const editModal = useDisclosure();

	/** Computed */
	const { incomingInvoice } = cell.row.original;
	const netAmount = getPayableAmounts(incomingInvoice).netAmount;
	const canEdit =
		netAmount !== null &&
		!isIncomingInvoiceLocked(incomingInvoice.state) &&
		!(!!citadel && citadel.cannot("update", "IncomingInvoice"));

	/** Guard */
	if (!canEdit) return <RowActionsWrapper to={null} />;

	/** Render */
	return (
		<React.Fragment>
			<RowActionsWrapper to={null}>
				<Tooltip label={t("incoming_invoices.edit_cost_allocation")}>
					<Button
						icon={<Icon name="Edit" />}
						size="xxs"
						variant="ghost"
						onClick={editModal.onOpen}
						data-cy="edit-cost-allocation-row-button"
					/>
				</Tooltip>
			</RowActionsWrapper>
			{editModal.isOpen && (
				<EditCostAllocationModal
					incomingInvoice={incomingInvoice}
					netAmount={netAmount}
					isOpen={editModal.isOpen}
					onClose={editModal.onClose}
				/>
			)}
		</React.Fragment>
	);
}
